<template>
	<fragment>
		<BaseHeader :title="$t('overview').toString()" />

		<OverviewSearch
			ref="overviewSearch"
			@search="search"
			@directSearch="directSearch"
			:is-search-running="isSearchRunning"
			:show-col="showCol"
			class="mb-8"
		></OverviewSearch>

		<div v-if="damageCases">
			<div class="flex justify-between align-middle mb-4 mx-4">
				<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="mr-4 self-center" />
				<div class="flex">
					<PaginationSizeSelect v-model="per_page" class="w-40" v-show="damageCases && damageCases.length" />
					<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" />
				</div>
			</div>
			<div class="flex flex-col">
				<div>
					<div class="align-middle inline-block min-w-full shadow overflow-hidden border-b border-gray-200">
						<div class="m-4" v-if="this.damageCases && this.damageCases.length <= 0">
							<b>{{ $t('no_entries_found') }}</b>
						</div>
						<table class="min-w-full" v-if="this.damageCases && this.damageCases.length > 0">
							<thead>
								<tr>
									<OverviewTableHeader
										:current-sort="currentSortField === 'created_at'"
										:current-sort-dir="currentSortDirection"
										:label="$t('date').toString()"
										@click.native="sortby('created_at')"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'licensePlate'"
										:current-sort-dir="currentSortDirection"
										:label="$t('license_plate').toString()"
										@click.native="sortby('licensePlate')"
										v-if="showCol([1, 2, 3, 5, 6, 7, 8])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'customer'"
										:current-sort-dir="currentSortDirection"
										:label="
											this.$store.getters.getAuthenticatedUserGroupLevel === '1' || this.$store.getters.getAuthenticatedUserGroupLevel === '2'
												? $t('customer')
												: $t('owner_name')
										"
										@click.native="sortby('customer')"
										v-if="showCol([1, 2, 3, 5, 6, 7])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'damageAssessor'"
										:current-sort-dir="currentSortDirection"
										:label="$t('damage_assessor').toString()"
										@click.native="sortby('damageAssessor')"
										v-if="showCol([1, 2, 8])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'vehicle'"
										:current-sort-dir="currentSortDirection"
										:label="$t('vehicle').toString()"
										@click.native="sortby('vehicle')"
										v-if="showCol([2, 3, 4, 5, 6, 7])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'repairShop'"
										:current-sort-dir="currentSortDirection"
										:label="isUserAllowedInLevel4 ? $t('transmitter').toString() : $t('mediator').toString()"
										@click.native="sortby('repairShop')"
										v-if="showCol([4, 8])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'intermediary'"
										:current-sort-dir="currentSortDirection"
										:label="$t('workshop').toString()"
										@click.native="sortby('intermediary')"
										v-if="showCol([1])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'lawyerOffice'"
										:current-sort-dir="currentSortDirection"
										:label="$t('lawyer').toString()"
										@click.native="sortby('lawyerOffice')"
										v-if="showCol([1])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'agent'"
										:current-sort-dir="currentSortDirection"
										:label="$t('broker').toString()"
										@click.native="sortby('agent')"
										v-if="showCol([1])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'damage_assessor_reference_number'"
										:current-sort-dir="currentSortDirection"
										:label="$t('az').toString()"
										@click.native="sortby('damage_assessor_reference_number')"
										v-if="showCol([2])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'lawyers_office_reference_number'"
										:current-sort-dir="currentSortDirection"
										:label="$t('az').toString()"
										@click.native="sortby('lawyers_office_reference_number')"
										v-if="showCol([3])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'repair_shop_reference_number'"
										:current-sort-dir="currentSortDirection"
										:label="$t('az').toString()"
										@click.native="sortby('repair_shop_reference_number')"
										v-if="showCol([5, 6])"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'imd_net'"
										:current-sort-dir="currentSortDirection"
										:label="$t('imd_connect').toString()"
										@click.native="sortby('imd_net')"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'status'"
										:current-sort-dir="currentSortDirection"
										:label="$t('status').toString()"
										@click.native="sortby('status')"
										v-if="showCol([2, 3, 5, 6]) && showStatus"
									/>
									<th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-center" v-if="showCol([1, 2, 3, 5, 6, 8])"></th>
								</tr>
							</thead>
							<tbody class="bg-white">
								<tr
									v-for="damageCase in this.damageCases"
									:key="damageCase.id"
									class="tr odd:bg-gray-200 cursor-pointer"
									@click="redirectToTarget(damageCase.id)"
								>
									<td class="px-6 py-4 border-b border-gray-200">
										{{ formatDate(damageCase.created_at) }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([1, 2, 3, 5, 6, 7, 8])">
										{{ damageCase.vehicle.licensePlate.text }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([1, 2, 3, 5, 6, 7])">
										{{ damageCase.customer.gender === 'company' ? damageCase.customer.companyName : damageCase.customer.lastName }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([1, 2, 8])">
										{{
											damageCase.damageAssessor.lastName !== ''
												? concatArray([damageCase.damageAssessor.lastName + ',', damageCase.damageAssessor.firstName])
												: ''
										}}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([2, 3, 4, 5, 6, 7])">
										{{ damageCase.vehicle.manufacturer }} {{ damageCase.vehicle.model }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([1])">
										<label class="flex-grow flex-shrink-0 break-words leading-none cursor-pointer">{{ damageCase.intermediary.company }}</label>
										<label class="text-gray-600  flex-grow flex-shrink-0 break-words leading-none block text-sm">{{
											damageCase.intermediary.user
										}}</label>
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([4, 8])">
										{{ damageCase.repairShop.name }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([2])">
										{{ damageCase.damageAssessorReferenceNumber }}
									</td>
									<td class="px-6 py-4border-b border-gray-200" v-if="showCol([3])">
										{{ damageCase.lawyerOfficeReferenceNumber }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([5, 6])">
										{{ damageCase.repairShopReferenceNumber }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([1])">
										{{ damageCase.lawyerOffice.name }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([1])">
										{{ damageCase.agent.firstName }} {{ damageCase.agent.lastName }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200">
										{{ damageCase.imdNetId }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200" v-if="showCol([2, 3, 5, 6]) && showStatus">
										<font-awesome-icon
											:icon="['fa', 'lock-open']"
											class="w-4 h-4 text-connectProBlue"
											size="lg"
											:class="{ hidden: damageCase.isClosed }"
										></font-awesome-icon>
										<font-awesome-icon
											:icon="['fa', 'lock']"
											class="w-4 h-4 text-connectProBlue"
											size="lg"
											:class="{ hidden: !damageCase.isClosed }"
										></font-awesome-icon>
									</td>
									<td class="px-6 py-4 border-b border-gray-200 text-sm leading-5 font-medium text-center" v-if="showCol([1, 2, 3, 5, 6, 8])">
										<div class="bg-sp btn block btn-overview-edit">
											<font-awesome-icon :icon="['fa', 'edit']" class="w-4 h-4 text-white" size="lg"></font-awesome-icon>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<div class="flex justify-between align-middle mb-4 ml-4 mt-4">
				<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="mr-4 self-center" />
				<div class="flex">
					<PaginationSizeSelect v-model="per_page" class="mr-4 w-40" v-show="damageCases && damageCases.length" />
					<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" class="mr-4" />
				</div>
			</div>
		</div>
		<div v-else-if="isUserAllowedInLevel2 || isUserAllowedInLevel3" class="main">
			<div>
				<BaseCard class="w-full bg-gray-200" :title="$t('own_last_process').toString()" :is-toggleable="false">
					<DashboardProcessList class="w-full flex" :processes="processesLastOwn" v-if="processesLastOwn" />

					<template #icons>
						<BaseCheckbox
							:filter-only="true"
							:label="$t('with_closed')"
							class="mb-0 mt-2"
							v-model="inclusiveClosedProcessesLastOwn"
							@input="getDashboard"
						/>
					</template>
				</BaseCard>

				<BaseCard
					class="mt-8 w-full bg-gray-200"
					:title="$t('last_process').toString()"
					:is-toggleable="false"
					v-if="(isUserAllowedInLevel2 && this.isAuthenticatedUserInGroupAdmin) || this.isUserAllowedInLevel3"
				>
					<DashboardProcessList :processes="processesLast" v-if="processesLast" />
					<template #icons>
						<BaseCheckbox
							:filter-only="true"
							:label="$t('with_closed')"
							class="mb-0 mt-2"
							v-model="inclusiveClosedProcessesLast"
							@input="getDashboard"
						/>
					</template>
				</BaseCard>
			</div>
		</div>
	</fragment>
</template>

<script>
import { Fragment } from 'vue-fragment';
import { DamagecasesApiFactory } from '@/api/generated';
import { concatArray, getStringFromDate } from '@/plugins/Utils';
import apiDefaults from '@/api/defaultConfiguration';
import BaseHeader from '@/components/navigation/BaseHeader';
import OverviewSearch from '@/components/damagecases/forms/OverviewSearch';
import OverviewTableHeader from '@/views/damagecases/OverviewTableHeader';
import BasePagination from '@/components/forms/buttons/BasePagination';
import PaginationSizeSelect from '@/components/damagecases/forms/PaginationSizeSelect';
import PaginationNumberOfPages from '@/components/forms/buttons/PaginationNumberOfPages';
import Authentication from '@/mixins/Authentication';
import BaseCard from '@/components/damagecases/forms/BaseCard.vue';
import DashboardProcessList from '@/views/Dashboard/ProcessList.vue';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox.vue';
import DashboardRepository from '@/api/repositories/DashboardRepository';

export default {
	name: 'Overview',
	mixins: [Authentication],
	components: {
		BaseCheckbox,
		DashboardProcessList,
		BaseCard,
		PaginationNumberOfPages,
		PaginationSizeSelect,
		BasePagination,
		OverviewTableHeader,
		OverviewSearch,
		BaseHeader,
		Fragment,
	},
	computed: {
		showAddDamageCaseButton() {
			const isLevel5or6AndMobile = this.isUserAllowedInLevel5or6 && window.appcontainer.isiOSAppcontainer;
			return isLevel5or6AndMobile || this.isUserAllowedInLevel2;
		},
		showStatus() {
			return this.$refs['overviewSearch'].status.value === 'all';
		},
	},
	data() {
		return {
			per_page: { display: '25', value: 25 },
			damageCases: null,
			searchQuery: undefined,
			timeoutIndex: 0,
			isSearchRunning: false,
			currentSortField: 'created_at',
			currentSortDirection: 'desc',
			current_page: 1,
			last_page: 1,
			total: 0,
			blockSearch: false,
			blockPageReset: false,
			inclusiveClosedProcessesLast: false,
			inclusiveClosedProcessesLastOwn: false,
			processesLast: null,
			processesLastOwn: null,
		};
	},
	mounted() {
		this.$emit('scrollUp', this.pageIndex);
		if (this.isUserAllowedInLevel2 || this.isUserAllowedInLevel3) {
			this.getDashboard();
		} else {
			this.$refs['overviewSearch'].directSearch();
		}
	},
	watch: {
		current_page: function(oldValue, newValue) {
			if (oldValue !== newValue) {
				this.blockPageReset = true;
				this.$refs['overviewSearch'].directSearch();
			}
		},
		per_page: function() {
			this.$refs['overviewSearch'].directSearch();
		},
	},
	methods: {
		concatArray,
		redirectToTarget(damageCaseId) {
			this.$router.push({
				name: 'damagecases.edit',
				params: { id: damageCaseId },
			});
		},
		getDashboard() {
			const options = {
				inclusiveClosedProcessesLast: this.inclusiveClosedProcessesLast,
				inclusiveClosedProcessesLastOwn: this.inclusiveClosedProcessesLastOwn,
			};
			new DashboardRepository().getDashboard(options).then(response => {
				this.processesLast = response.data.processesLast;
				this.processesLastOwn = response.data.processesLastOwn;
			});
		},
		search($event) {
			clearTimeout(this.timeoutIndex);
			this.timeoutIndex = setTimeout(() => {
				this.directSearch($event);
			}, 300);
		},
		directSearch($event) {
			if (this.blockSearch) {
				return;
			}
			if (!this.blockPageReset) {
				this.current_page = 1;
			}

			this.blockSearch = true;
			this.isSearchRunning = true;
			const sort = (this.currentSortDirection === 'desc' ? '-' : '') + this.currentSortField;

			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesGet(
					this.current_page,
					this.per_page ? this.per_page.value : undefined,
					sort,
					$event.range ? $event.range : undefined,
					$event.year ? $event.year : undefined,
					$event.status ? $event.status : undefined,
					$event.damageAssessorReferenceNumber ? $event.damageAssessorReferenceNumber : undefined,
					$event.lawyerOfficeReferenceNumber ? $event.lawyerOfficeReferenceNumber : undefined,
					$event.repairShopReferenceNumber ? $event.repairShopReferenceNumber : undefined,
					$event.vehicles ? $event.vehicles : undefined,
					$event.imd_net ? $event.imd_net : undefined,
					$event.customer ? $event.customer : undefined,
					undefined,
					$event.customer ? $event.customer : undefined,
					$event.repairshop ? $event.repairshop : undefined,
					$event.customer ? $event.customer : undefined,
					(this.isUserAllowedInLevel5or6 || this.isUserAllowedInLevel2) && this.isAuthenticatedUserInGroupMitarbeiter && $event.status === 'opened',
				)
				.then(response => {
					this.damageCases = response.data.data;
					this.current_page = response.data.meta.current_page;
					this.last_page = response.data.meta.last_page;
					this.total = response.data.meta.total;
					this.isSearchRunning = false;
					this.blockSearch = false;
					this.blockPageReset = false;
				})
				.catch(error => {
					this.isSearchRunning = false;
				});
		},
		showCol: function(showLvl) {
			return showLvl.indexOf(parseInt(this.$store.getters.getAuthenticatedUserGroupLevel)) !== -1;
		},
		sortby: function(field) {
			if (field === this.currentSortField) {
				this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
			} else {
				this.currentSortDirection = 'asc';
			}
			this.currentSortField = field;
			this.$refs['overviewSearch'].directSearch();
		},
		formatDate: function(timestamp) {
			const date = new Date(timestamp * 1000);
			return getStringFromDate(date);
		},
	},
};
</script>
